/**
 * useAuth — composable with module-level reactive state.
 *
 * Pattern adapted from adview_system/frontend/src/composables/useAuth.js.
 * Module-level refs mean all callers share the same auth state without
 * having to go through a Pinia store (which would also work, but this is
 * lighter and matches the reference implementation).
 */
import { ref } from "vue";
import {
  AuthExpiredError,
  fetchCurrentUser,
  getToken,
  hasToken,
  login as apiLogin,
  logout as apiLogout,
  type CurrentUser,
} from "@/api/auth";

// ---- Module-level state (shared across all useAuth() callers) ----
const user = ref<CurrentUser | null>(null);
const userRole = ref<string | null>(null); // "admin" | "ops" | "manager" | "viewer" | "cv_worker"
const isAuthenticated = ref(false);
const initialized = ref(false);
const loading = ref(false);

function detectRole(u: CurrentUser | null): string | null {
  if (!u || !u.role) return null;
  const role = u.role;
  if (typeof role === "object" && role.name) {
    const name = role.name.toLowerCase();
    if (name.includes("admin")) return "admin";
    if (name.includes("ops")) return "ops";
    if (name.includes("manager")) return "manager";
    if (name.includes("viewer")) return "viewer";
    if (name.includes("cv")) return "cv_worker";
  }
  return null;
}

function clearAuthState() {
  user.value = null;
  userRole.value = null;
  isAuthenticated.value = false;
}

export function useAuth() {
  async function init() {
    if (initialized.value) return;
    loading.value = true;
    try {
      if (getToken()) {
        const data = await fetchCurrentUser();
        if (data) {
          user.value = data;
          userRole.value = detectRole(data);
          isAuthenticated.value = true;
        } else {
          clearAuthState();
        }
      }
    } catch (e) {
      console.error("[Auth] init error:", e);
      clearAuthState();
    } finally {
      initialized.value = true;
      loading.value = false;
    }
  }

  async function login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    loading.value = true;
    try {
      await apiLogin(email, password);
      const data = await fetchCurrentUser();
      if (data) {
        user.value = data;
        userRole.value = detectRole(data);
        isAuthenticated.value = true;
        initialized.value = true;
      }
      return { success: true };
    } catch (e) {
      console.error("[Auth] Login error:", e);
      return { success: false, error: (e as Error).message || "Login failed" };
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    await apiLogout();
    clearAuthState();
    initialized.value = false;
  }

  function handleAuthExpired() {
    if (!isAuthenticated.value) return;
    console.warn("[Auth] Session expired — redirecting to login");
    clearAuthState();
    initialized.value = false;
    window.location.href = "/login";
  }

  return {
    user,
    userRole,
    isAuthenticated,
    initialized,
    loading,
    hasToken,
    init,
    login,
    logout,
    handleAuthExpired,
  };
}

export { AuthExpiredError };
